import React from "react";
//#region types
import { FormFieldProps, AutoCompleteExtraProps } from "models/propTypes/genericFormProps";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
//#endregion
//#region components
import usePlaceholder from "../hooks/usePlaceholder";
import AutoCompleteInput from "../baseFields/autoCompleteInput";
import ReadOnlyField from "../baseFields/readOnlyField";
import { FormControl } from "@mui/material";
import { FormikProps, useFormikContext } from "formik";
//#endregion
import { LocalizationHelperInstance, FormHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

function AutoCompleteTextFormField<T extends Record<string, any>>(props: FormFieldProps<T>) {
  React.useImperativeHandle(props.fieldRef, () => ({
    change: (value: AutocompleteListItem | undefined) => {
      onChangeCustom(value ?? null);
    },
  }));

  const {
    name,
    field,
    errors,
    value,
    handleOnChangeCustom,
    handleOnBlurCustom,
    success,
    optional,
    readonly,
    loading,
    disabled,
  } = props;
  const hasError = !!errors;

  const extraProps = field.extraProps as AutoCompleteExtraProps<T>;
  const formikContext = useFormikContext<T>();

  const placeholder = usePlaceholder(field.label, field.placeholder, optional);

  const onChangeCustom = React.useCallback(
    (newValue: AutocompleteListItem | null) => {
      handleOnChangeCustom(
        name,
        newValue?.value,
        false,
        extraProps.referenceFieldName
          ? FormHelper.formatReferenceFieldName(name, extraProps.referenceFieldName as string)
          : undefined,
        newValue?.id
      );

      setTimeout(() => {
        if (newValue === null) extraProps.onItemClear && extraProps.onItemClear(name, formikContext);
        else extraProps.onItemSelect && extraProps.onItemSelect(newValue, name, formikContext);
      });
    },
    [name, formikContext]
  );

  const getItems = async (
    value: string,
    formattedValue?: string | undefined,
    formikProps?: FormikProps<any> | undefined
  ) => {
    let vals: AutocompleteListItem[] | undefined = [];
    if (typeof extraProps.items === "function") {
      vals = await extraProps.items(value, formattedValue, formikContext);
    }
    return vals;
  };

  const component = React.useMemo(
    () => (
      <AutoCompleteInput
        label={LocalizationHelperInstance.getFormattedMessage(field.label)}
        placeholder={placeholder}
        value={extraProps.formatInputValue ? extraProps.formatInputValue(value && `${value}`) : value && `${value}`}
        name={name}
        isNumericString={extraProps.isNumericString}
        allowNegative={extraProps.allowNegative}
        valueSuffix={extraProps.valueSuffix}
        freeSolo={extraProps.freeSolo}
        error={hasError}
        success={success}
        onItemSelect={onChangeCustom}
        items={typeof extraProps.items === "function" ? getItems : extraProps.items}
        suggestedItems={extraProps.suggestedItems}
        actions={extraProps.actions}
        includeNone={extraProps.includeNone}
        onBlur={handleOnBlurCustom.bind(null, name)}
        onFocus={field.onFocusHandler}
        icon={field.icon}
        disabled={disabled}
        disableClear={extraProps.disableClear}
        isLoading={loading}
        maxLength={field.maxLength}
        className={field.classes?.input}
        format={extraProps.mask}
        itemTemplate={extraProps.itemTemplate}
      />
    ),
    [onChangeCustom, errors, value, success, name, placeholder, extraProps]
  );

  return readonly ? (
    <ReadOnlyField label={field.label} href={field.href} className={cssExports.readOnlyTextField}>
      {extraProps?.valueSuffix ? `${value} ${extraProps.valueSuffix}` : value}
    </ReadOnlyField>
  ) : (
    <FormControl fullWidth className={clsx(cssExports.inputFormControl)}>
      {component}
    </FormControl>
  );
}

export default AutoCompleteTextFormField;
